import { ChevronDown, RotateCcw, SlidersHorizontal } from 'lucide-react'
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import type { ApartmentFilterOptions } from '@services/livability/types'

import { cn } from '@/lib/utils'
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback'
import type { ApartmentFilters } from '../../utils/apartmentFilterUrl'
import { hasActiveApartmentFilters } from '../../utils/apartmentFilterUrl'
import { applyFilterPatch, formatCityLabel } from './filterDefs'

const RANGE_DEBOUNCE_MS = 350

const ROOM_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: n === 5 ? '5+' : String(n)
}))

interface FilterBarProps {
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
  onClearAll: () => void
  filterOptions?: ApartmentFilterOptions
  /** Control rendered before the price filter (e.g. rent/sale toggle). */
  leading?: ReactNode
  /** Content rendered at the far right, after the clear button (e.g. result count). */
  trailing?: ReactNode
  /** Extra controls (e.g. the "more filters" dialog trigger). */
  children?: ReactNode
  className?: string
}

export function FilterBar({
  filters,
  onChange,
  onClearAll,
  filterOptions,
  leading,
  trailing,
  children,
  className
}: FilterBarProps) {
  const hasActive = hasActiveApartmentFilters(filters)

  return (
    <div
      role="group"
      aria-label="Filtre bytov"
      className={cn(
        'flex flex-wrap items-end gap-x-3 gap-y-2.5 rounded-2xl bg-white p-3 shadow-sm',
        className
      )}
    >
      {leading}

      <FilterControl label="Cena">
        <PriceInputs filters={filters} onChange={onChange} />
      </FilterControl>

      <FilterControl label="Izby">
        <RoomsSegment filters={filters} onChange={onChange} />
      </FilterControl>

      <FilterControl label="Lokalita">
        <LocationPopover
          filters={filters}
          filterOptions={filterOptions}
          onChange={onChange}
        />
      </FilterControl>

      {children && (
        <>
          <div className="mx-0.5 hidden h-7 self-end border-l border-ink/[0.08] sm:block" />
          {children}
        </>
      )}

      <div className="ml-auto flex h-7 items-center gap-3 self-end">
        {hasActive && (
          <button
            type="button"
            onClick={onClearAll}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-full px-3',
              'text-[13px] font-medium text-ink/50 transition-colors',
              'outline-none hover:bg-ink/[0.04] hover:text-ink',
              'focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1'
            )}
            aria-label="Vymazať filtre"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Vymazať</span>
          </button>
        )}
        {trailing}
      </div>
    </div>
  )
}

/** Column with a small uppercase label above the control. */
export function FilterControl({
  label,
  children
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-ink/50">
        {label}
      </span>
      {children}
    </div>
  )
}

/** Filled trigger button used by the popover-based controls. */
const FillTrigger = forwardRef<
  HTMLButtonElement,
  { value: string | null; placeholder: string } & Omit<
    ComponentPropsWithoutRef<'button'>,
    'value'
  >
>(function FillTrigger({ value, placeholder, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-[10px] bg-ink/[0.04] px-2.5 text-[13px]',
        'outline-none transition-colors hover:bg-ink/[0.07]',
        'focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1',
        value ? 'font-semibold text-ink' : 'font-medium text-ink/40',
        className
      )}
    >
      {value ?? placeholder}
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink/40" />
    </button>
  )
})

/** Ghost trigger for the "more filters" dialog, styled to sit in the bar. */
export function MoreFiltersTrigger({
  activeCount,
  onClick
}: {
  activeCount: number
  onClick?: () => void
}) {
  return (
    <FilterControl label="&nbsp;">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex h-7 items-center gap-1.5 rounded-[10px] bg-ink/[0.04] px-2.5',
          'text-[13px] font-semibold text-ink outline-none transition-colors hover:bg-ink/[0.07]',
          'focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1'
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 text-ink/60" />
        Viac filtrov
        {activeCount > 0 && (
          <span className="grid h-[19px] min-w-[19px] place-items-center rounded-full bg-primary px-1 text-[11px] font-bold text-white">
            {activeCount}
          </span>
        )}
      </button>
    </FilterControl>
  )
}

const priceInputClass = cn(
  'h-7 w-16 rounded-[10px] border-0 bg-ink/[0.04] px-2 py-0 text-[13px] font-semibold text-ink',
  'shadow-none outline-none transition-colors',
  'placeholder:font-medium placeholder:text-ink/40',
  'focus-visible:ring-2 focus-visible:ring-primary/25',
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
)

function PriceInputs({
  filters,
  onChange
}: {
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
}) {
  const [min, setMin] = useState(filters.minPrice?.toString() ?? '')
  const [max, setMax] = useState(filters.maxPrice?.toString() ?? '')
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const { debounced: commitDebounced, flush, cancel } = useDebouncedCallback(
    (nextMin: string, nextMax: string) => {
      const parsedMin = nextMin.trim() ? Number(nextMin) : undefined
      const parsedMax = nextMax.trim() ? Number(nextMax) : undefined
      onChange(
        applyFilterPatch(filtersRef.current, {
          minPrice: Number.isFinite(parsedMin) ? parsedMin : undefined,
          maxPrice: Number.isFinite(parsedMax) ? parsedMax : undefined
        })
      )
    },
    RANGE_DEBOUNCE_MS
  )

  useEffect(() => {
    if (filters.minPrice === undefined && filters.maxPrice === undefined) {
      cancel()
      setMin('')
      setMax('')
    }
  }, [cancel, filters.minPrice, filters.maxPrice])

  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        inputMode="numeric"
        aria-label="Cena od"
        placeholder="Min €"
        value={min}
        onChange={(e) => {
          setMin(e.target.value)
          commitDebounced(e.target.value, max)
        }}
        onBlur={flush}
        className={priceInputClass}
      />
      <span className="text-ink/40">–</span>
      <Input
        type="number"
        inputMode="numeric"
        aria-label="Cena do"
        placeholder="Max €"
        value={max}
        onChange={(e) => {
          setMax(e.target.value)
          commitDebounced(min, e.target.value)
        }}
        onBlur={flush}
        className={priceInputClass}
      />
    </div>
  )
}

function RoomsSegment({
  filters,
  onChange
}: {
  filters: ApartmentFilters
  onChange: (next: ApartmentFilters) => void
}) {
  const selected = filters.roomCounts ?? []

  return (
    <div
      role="group"
      aria-label="Počet izieb"
      className="inline-flex h-7 items-center gap-0.5 rounded-[10px] bg-ink/[0.04] p-[2px]"
    >
      {ROOM_OPTIONS.map((option) => {
        const isSelected = selected.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => {
              const next = isSelected
                ? selected.filter((v) => v !== option.value)
                : [...selected, option.value].sort((a, b) => a - b)
              onChange(
                applyFilterPatch(filters, {
                  roomCounts: next.length > 0 ? next : undefined
                })
              )
            }}
            className={cn(
              'grid h-6 w-7 place-items-center rounded-[8px] text-[13px] font-semibold',
              'outline-none transition-all focus-visible:ring-2 focus-visible:ring-primary/25',
              isSelected
                ? 'bg-primary text-white shadow-[0_2px_8px_rgba(17,173,187,0.35)]'
                : 'text-ink/60 hover:text-ink'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function LocationPopover({
  filters,
  filterOptions,
  onChange
}: {
  filters: ApartmentFilters
  filterOptions?: ApartmentFilterOptions
  onChange: (next: ApartmentFilters) => void
}) {
  const districts = filters.city
    ? filterOptions?.districts?.[filters.city.toLowerCase()] ?? []
    : []

  const value = filters.city
    ? filters.district
      ? `${formatCityLabel(filters.city)} – ${formatCityLabel(filters.district)}`
      : formatCityLabel(filters.city)
    : null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <FillTrigger value={value} placeholder="Všade" />
      </PopoverTrigger>
      <PopoverContent align="start" className="app-chrome w-72">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Mesto</Label>
            <Select
              value={filters.city ?? undefined}
              onValueChange={(value) =>
                onChange(
                  applyFilterPatch(filters, {
                    city: value,
                    district: undefined
                  })
                )
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Vybrať mesto" />
              </SelectTrigger>
              <SelectContent>
                {(filterOptions?.cities ?? []).map((city) => (
                  <SelectItem key={city} value={city}>
                    {formatCityLabel(city)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {filters.city && districts.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                Mestská časť
              </Label>
              <Select
                value={filters.district ?? undefined}
                onValueChange={(value) =>
                  onChange(applyFilterPatch(filters, { district: value }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Vybrať mestskú časť" />
                </SelectTrigger>
                <SelectContent>
                  {districts.map((district) => (
                    <SelectItem key={district} value={district}>
                      {formatCityLabel(district)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {filters.city && (
            <button
              type="button"
              onClick={() =>
                onChange(
                  applyFilterPatch(filters, {
                    city: undefined,
                    district: undefined
                  })
                )
              }
              className="text-xs font-medium text-ink/50 underline-offset-2 hover:text-ink hover:underline"
            >
              Zrušiť lokalitu
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
